import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, PauseCircle, CheckCircle2, Trash2, Loader2, X,
  Users, DollarSign, MapPin, Calendar, MessageSquare, Eye,
} from "lucide-react";

const RESPITE = [
  ["traditional_respite", "Traditional respite"],
  ["overnight_support", "Overnight support"],
  ["travel_assistance", "Travel assistance"],
  ["party_buffers", "Party buffers"],
  ["community_support", "Community support"],
];

const STATUS_LABEL = { active: "Active", paused: "Paused", filled: "Filled", closed: "Closed" };
const STATUS_STYLE = {
  active: "chip-ochre",
  paused: "chip",
  filled: "chip",
  closed: "chip",
};

const EMPTY_FORM = {
  title: "", description: "", respite_services_needed: [],
  frequency: "occasional", schedule_notes: "",
  hourly_rate_offered: "", zip_code: "", max_distance_miles: 20,
  child_ages_summary: "", required_certifications: [], aba_required: false, overnight_needed: false,
};

function ListingForm({ initial, onSubmit, onCancel, submitting }) {
  const [f, setF] = useState({ ...EMPTY_FORM, ...(initial || {}) });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggle = (arrKey, item) => setF((s) => ({
    ...s,
    [arrKey]: (s[arrKey] || []).includes(item)
      ? s[arrKey].filter((x) => x !== item)
      : [...(s[arrKey] || []), item],
  }));

  const submit = (e) => {
    e.preventDefault();
    if (!f.title.trim() || f.title.length < 4) return toast.error("Title must be at least 4 characters");
    if (!f.description.trim() || f.description.length < 10) return toast.error("Description must be at least 10 characters");
    if (!f.zip_code.trim()) return toast.error("ZIP code is required");
    const payload = {
      ...f,
      hourly_rate_offered: f.hourly_rate_offered === "" ? null : Number(f.hourly_rate_offered),
      max_distance_miles: f.max_distance_miles === "" ? null : Number(f.max_distance_miles),
      required_certifications: (f.required_certifications || []).filter(Boolean),
    };
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="field-label">Title</label>
        <input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="listing-title"
          maxLength={120} placeholder="Need Saturday overnight care for our 8yo"
          className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
      </div>
      <div>
        <label className="field-label">Description</label>
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={5}
          data-testid="listing-description" maxLength={4000}
          placeholder="Describe your child(ren), their needs, sensory preferences, and what a great match looks like."
          className="w-full rounded-lg border border-stone2-200 px-4 py-3 text-sm focus:ring-2 focus:ring-sage-500" />
      </div>

      <div>
        <label className="field-label">Respite services needed</label>
        <div className="flex flex-wrap gap-2">
          {RESPITE.map(([k, l]) => {
            const active = (f.respite_services_needed || []).includes(k);
            return (
              <button type="button" key={k} data-testid={`listing-respite-${k}`}
                onClick={() => toggle("respite_services_needed", k)}
                className={active ? "chip-ochre" : "chip"}>
                {l}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="field-label">Frequency</label>
          <select value={f.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="listing-frequency"
            className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm">
            <option value="one_time">One time</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every 2 weeks</option>
            <option value="monthly">Monthly</option>
            <option value="occasional">Occasional / as needed</option>
          </select>
        </div>
        <div>
          <label className="field-label">Hourly rate offered ($/hr)</label>
          <input type="number" min="0" step="1" value={f.hourly_rate_offered}
            onChange={(e) => set("hourly_rate_offered", e.target.value)} data-testid="listing-rate"
            placeholder="e.g. 25"
            className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm" />
        </div>
      </div>

      <div>
        <label className="field-label">Schedule notes</label>
        <input value={f.schedule_notes} onChange={(e) => set("schedule_notes", e.target.value)}
          data-testid="listing-schedule" placeholder="Saturdays 6pm-11pm; occasional weekday afternoons"
          className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="field-label">ZIP code</label>
          <input value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="listing-zip"
            className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm" />
        </div>
        <div>
          <label className="field-label">Max distance (miles)</label>
          <input type="number" min="1" max="200" value={f.max_distance_miles}
            onChange={(e) => set("max_distance_miles", e.target.value)} data-testid="listing-distance"
            className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm" />
        </div>
      </div>

      <div>
        <label className="field-label">Child ages / brief description</label>
        <input value={f.child_ages_summary} onChange={(e) => set("child_ages_summary", e.target.value)}
          data-testid="listing-ages" maxLength={200}
          placeholder="8yo (ASD Level 2), 5yo NT"
          className="w-full rounded-lg border border-stone2-200 px-4 py-2.5 text-sm" />
      </div>

      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 text-sm text-sage-900">
          <input type="checkbox" checked={f.aba_required} onChange={(e) => set("aba_required", e.target.checked)}
            data-testid="listing-aba-required" className="rounded text-sage-600" />
          ABA training required
        </label>
        <label className="flex items-center gap-2 text-sm text-sage-900">
          <input type="checkbox" checked={f.overnight_needed} onChange={(e) => set("overnight_needed", e.target.checked)}
            data-testid="listing-overnight-required" className="rounded text-sage-600" />
          Overnight care needed
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="btn-secondary" data-testid="listing-form-cancel">
            Cancel
          </button>
        )}
        <button type="submit" disabled={submitting} className="btn-primary" data-testid="listing-form-submit">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {initial ? "Save changes" : "Post listing"}
        </button>
      </div>
    </form>
  );
}

export default function FamilyListings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/family-listings/mine");
      setItems(data.results || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createOne = async (payload) => {
    setSubmitting(true);
    try {
      await api.post("/family-listings", payload);
      toast.success("Listing posted");
      setCreating(false);
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSubmitting(false); }
  };

  const updateOne = async (payload) => {
    setSubmitting(true);
    try {
      await api.patch(`/family-listings/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSubmitting(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/family-listings/${id}/status`, { status });
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this listing? Sitters won't see it anymore.")) return;
    try {
      await api.delete(`/family-listings/${id}`);
      toast.success("Listing deleted");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="family-listings-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="section-eyebrow">Your care needs</span>
            <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Listings</h1>
            <p className="text-stone2-600 text-sm mt-1 max-w-lg">
              Post what kind of care you're looking for. Specialized sitters browse these listings and
              reach out to you directly. Photos and children's details stay private until you reply.
            </p>
          </div>
          {!creating && !editing && (
            <button className="btn-primary" onClick={() => setCreating(true)} data-testid="listings-new">
              <Plus className="w-4 h-4" />
              New listing
            </button>
          )}
        </div>

        {(creating || editing) && (
          <section className="paper-card p-6 sm:p-7 mt-6" data-testid="listing-form-card">
            <h2 className="font-heading font-semibold text-sage-900 text-lg mb-4">
              {editing ? "Edit listing" : "New listing"}
            </h2>
            <ListingForm
              initial={editing || null}
              onSubmit={editing ? updateOne : createOne}
              onCancel={() => { setCreating(false); setEditing(null); }}
              submitting={submitting}
            />
          </section>
        )}

        <div className="mt-6 space-y-3">
          {loading && <div className="paper-card p-8 text-center text-stone2-500">Loading…</div>}
          {!loading && items.length === 0 && !creating && (
            <div className="paper-card p-8 text-center">
              <p className="text-stone2-600">You haven't posted a care listing yet.</p>
              <button className="btn-primary mt-3" onClick={() => setCreating(true)}>Post your first listing</button>
            </div>
          )}
          {items.map((l) => (
            <article key={l.id} className="paper-card p-5" data-testid={`listing-card-${l.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={STATUS_STYLE[l.status] || "chip"}>{STATUS_LABEL[l.status] || l.status}</span>
                    <span className="text-xs text-stone2-500">Posted {new Date(l.created_at).toLocaleDateString()}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-sage-900 text-lg leading-tight">{l.title}</h3>
                  <p className="mt-1 text-sm text-stone2-600 line-clamp-2">{l.description}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone2-600">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {l.zip_code} · {l.max_distance_miles}mi</span>
                    {l.hourly_rate_offered && (
                      <span className="inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> ${l.hourly_rate_offered}/hr</span>
                    )}
                    {l.schedule_notes && (
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {l.schedule_notes}</span>
                    )}
                    {l.child_ages_summary && (
                      <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {l.child_ages_summary}</span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(l.respite_services_needed || []).map((s) => (
                      <span key={s} className="chip">{(RESPITE.find(([k]) => k === s) || [s, s])[1]}</span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-stone2-500">
                    <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {l.view_count} views</span>
                    <Link to="/messages" className="inline-flex items-center gap-1 text-sage-700 font-semibold">
                      <MessageSquare className="w-3.5 h-3.5" /> {l.message_count} sitter messages
                    </Link>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <button className="btn-secondary text-xs" onClick={() => { setEditing(l); setCreating(false); }} data-testid={`listing-edit-${l.id}`}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  {l.status === "active" && (
                    <>
                      <button className="btn-secondary text-xs" onClick={() => setStatus(l.id, "filled")} data-testid={`listing-fill-${l.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Fill
                      </button>
                      <button className="btn-secondary text-xs" onClick={() => setStatus(l.id, "paused")} data-testid={`listing-pause-${l.id}`}>
                        <PauseCircle className="w-3.5 h-3.5" /> Pause
                      </button>
                    </>
                  )}
                  {l.status !== "active" && (
                    <button className="btn-secondary text-xs" onClick={() => setStatus(l.id, "active")} data-testid={`listing-reactivate-${l.id}`}>
                      Reactivate
                    </button>
                  )}
                  <button className="btn-secondary text-xs text-red-700 hover:bg-red-50" onClick={() => removeOne(l.id)} data-testid={`listing-delete-${l.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
